import React, { memo, useMemo, ChangeEvent, MouseEvent, useState } from 'react'
import classNames from 'classnames'
import { Card } from 'transfix-ui/components/Card'
import { Spinner } from 'transfix-ui/components/Spinner'
import { TAG_SIZE, TAG_STYLE, Tag } from 'transfix-ui/components/Tag'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColorsLegacy } from 'transfix-ui/theme/legacyColors'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import uuid from 'uuid'
import { CarrierInlineDisplay } from 'apps/fleet-planner/components/atoms/CarrierInlineDisplay'
import { DispatcherInlineDisplay } from 'apps/fleet-planner/components/atoms/DispatcherInlineDisplay'
import { DriverInlineDisplay } from 'apps/fleet-planner/components/atoms/DriverInlineDisplay'
import { LoadStatusHeader } from 'apps/fleet-planner/components/atoms/LoadStatusHeader'
import { TrailerInlineDisplay } from 'apps/fleet-planner/components/atoms/TrailerInlineDisplay'
import { TruckInlineDisplay } from 'apps/fleet-planner/components/atoms/TruckInlineDisplay'
import { Details } from 'apps/fleet-planner/components/load-details/Details'
import { InlineRow } from 'apps/fleet-planner/components/load-details/InlineRow'
import { DetailsHeader } from 'apps/fleet-planner/components/load-details/LoadDetailsInlineEdit'
import {
  NotesHeader,
  NotesDisplay,
} from 'apps/fleet-planner/components/load-details/LoadNotesInlineEdit'
import { AppointmentDetails } from 'apps/fleet-planner/components/load-details/stops/AppointmentInlineEdit'
import { StopDetailsDisplay } from 'apps/fleet-planner/components/load-details/stops/DetailsInlineEdit'
import { FacilityDetails } from 'apps/fleet-planner/components/load-details/stops/FacilityDetails'
import { InstructionDisplay } from 'apps/fleet-planner/components/load-details/stops/InstructionInlineEdit'
import { StopHeader } from 'apps/fleet-planner/components/load-details/stops/StopInlineEditContainer'
import { StopsSummary } from 'apps/fleet-planner/components/load-details/stops/StopsSummary'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  DRIVER_IN_OUT_FORM_STATE,
  useDriverInOutForm,
} from 'apps/fleet-planner/hooks/useDriverInOutForm'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import {
  IStop,
  ILoad,
  IShipmentStatusUpdate,
  IShipmentInOutUpdate,
  IGetShipmentResponse,
  IFile,
  IShipmentPodUpdate,
} from 'apps/fleet-planner/types'
import { addFileTypeToDocumentList } from 'apps/fleet-planner/utils/api'
import {
  getNextDriverStatus,
  CTA_STATUS_UPDATE_STATES,
} from 'apps/fleet-planner/utils/driverStatuses'
import { mapRemovedFiles } from 'apps/fleet-planner/utils/fileUtils'
import { getStopNumbers } from 'apps/fleet-planner/utils/stopNumbers'
import { standardFormats } from 'apps/fleet-planner/utils/userDateTimeSettings'
import { Container } from 'components/layout'
import { capitalize } from 'utils/capitalize'
import { filePromises } from 'utils/fileUploadHelper'
import { DISPLAY } from 'constants/display'
import { AttachmentList } from '../AttachmentList'
import { LoadStatusTag } from '../atoms/LoadStatusTag'
import { AddAttachment } from '../form-components/AddAttachment'
import { LoadDetailsFileHeader } from '../load-details/LoadDetailsFileHeader'
import { CheckInTimes } from '../load-details/stops/CheckInTimes'
import { CheckOutTimes } from '../load-details/stops/CheckOutTimes'
import { InOutTimesForm } from './InOutTimesForm'
import { StatusUpdateDrawer } from './StatusUpdateDrawer'
import { TrailerNumberForm } from './TrailerNumberForm'
// eslint-disable-next-line perfectionist/sort-imports
import 'apps/fleet-planner/scss/pages/_share-load.scss'

const useTopnavStyles = makeStyles(({ breakpoints, spacing }) => ({
  topNav: { flexWrap: 'wrap', paddingLeft: `${spacing(2)} !important` },
  uploadButton: {
    flexBasis: '100%',
    width: '100%',
    minWidth: 'max-content',
    paddingBottom: spacing(2),
  },
  readOnly: {
    '& .dpl-tag': {
      backgroundColor: `${brandColorsLegacy.steelLight}`,
      borderColor: `${brandColorsLegacy.steelLight}`,
    },
    '& .dpl-tag:hover': {
      borderColor: `${brandColorsLegacy.steelLight} !important`,
    },
    '& .dpl-tag *': {
      color: `${brandColorsLegacy.pavement} !important`,
    },
  },
  [breakpoints.up('md')]: {
    topNav: { flexWrap: 'none', paddingLeft: spacing(1) },
    uploadButton: {
      flexBasis: 'unset',
      width: 'unset',
      minWidth: 'unset',
      paddingBottom: 0,
    },
  },
}))

interface ShareLoadDetailsStopProps {
  isExternal?: boolean
  stop: IStop
  sequence: number
  onInOutTimeClick: (state: DRIVER_IN_OUT_FORM_STATE, stop: IStop) => void
}

export interface ISubmitOneTimeLocation {
  latitude: number
  longitude: number
  isManual: boolean
  externalStopId?: string
}

const ShareLoadDetailsStop = memo<ShareLoadDetailsStopProps>(
  ({ isExternal, onInOutTimeClick, sequence, stop }) => {
    const {
      appointmentNumber,
      bolNumber,
      checkedInAt,
      checkedOutAt,
      commodity,
      displayTimezone,
      endAt,
      instructions,
      isDropTrailer,
      poNumber,
      referenceNumber,
      startAt,
      stopType,
      unit,
      unitQuantity,
      weight,
    } = stop || {}
    const stopTypeTitle = capitalize(stopType)
    const inOutDetailClickHandler = (inOutTime: DRIVER_IN_OUT_FORM_STATE) =>
      onInOutTimeClick(inOutTime, stop)
    return (
      <>
        <Card className='mt4 mb4'>
          <Box display='flex' className='pl1'>
            <StopHeader stopType={stopType} sequence={sequence} />
            {isDropTrailer && (
              <div className='mt2 mb4'>
                <Tag
                  label='Drop Trailer'
                  size={TAG_SIZE.xsmall}
                  tagStyle={TAG_STYLE.info}
                  outlineOnly
                />
              </div>
            )}
          </Box>
          <InlineRow
            rowTitle={`${stopTypeTitle} Location`}
            rowBody={<FacilityDetails stop={stop} sequence={sequence} />}
          />
          <InlineRow
            rowTitle='Appointment'
            rowBody={
              <AppointmentDetails
                timeFormats={standardFormats}
                displayTimezone={displayTimezone}
                endAt={endAt}
                startAt={startAt}
                stopType={stopType}
                sequence={sequence}
              />
            }
          />
          <InlineRow
            rowTitle='Check In'
            containerClass='load-details-row-in-out-container'
            rowBody={
              <>
                <CheckInTimes
                  isEditable={isExternal ? false : !!checkedInAt}
                  stopType={stopType}
                  sequence={sequence}
                  checkedInAt={checkedInAt}
                  timeFormats={standardFormats}
                  onButtonClick={inOutDetailClickHandler}
                  displayTimezone={displayTimezone}
                />
                {checkedInAt && isExternal && (
                  <div className='row'>
                    <Typography color='textSecondary' variant='caption' component='div'>
                      This is the Check-In time Transfix has on file.
                    </Typography>
                  </div>
                )}
              </>
            }
          />
          <InlineRow
            rowTitle='Check Out'
            containerClass='load-details-row-in-out-container'
            rowBody={
              <>
                <CheckOutTimes
                  isEditable={isExternal ? false : !!checkedOutAt}
                  stopType={stopType}
                  sequence={sequence}
                  checkedOutAt={checkedOutAt}
                  timeFormats={standardFormats}
                  onButtonClick={inOutDetailClickHandler}
                  displayTimezone={displayTimezone}
                />
                {checkedOutAt && isExternal && (
                  <Typography color='textSecondary' variant='caption' component='div'>
                    This is the Check-Out time Transfix has on file.
                  </Typography>
                )}
              </>
            }
          />
          <InlineRow
            rowTitle={`${stopTypeTitle} Details`}
            rowBody={
              <StopDetailsDisplay
                bolNumber={bolNumber}
                referenceNumber={referenceNumber}
                stopType={stopType}
                sequence={sequence}
                commodity={commodity}
                weight={weight}
                unitQuantity={unitQuantity}
                unit={unit}
                poNumber={poNumber}
                appointmentNumber={appointmentNumber}
              />
            }
          />
          <InlineRow
            rowTitle={`${stopTypeTitle} Instructions`}
            rowBody={
              <InstructionDisplay
                dataTest={`${stopType}-${sequence}-stop-instructions`}
                instructions={instructions}
              />
            }
          />
        </Card>
      </>
    )
  }
)

interface ShareLoadDetailsDisplayProps {
  load?: ILoad
  hasToken: boolean
  shareToken: string
  isLoading: boolean
  isShipmentUpdating?: boolean
  onSubmit: (
    shipmentId: string,
    shipment: IShipmentStatusUpdate
  ) => Promise<IGetShipmentResponse | undefined>
  onEdit: (
    shipmentId: string,
    stopId: string,
    stop: IShipmentInOutUpdate
  ) => Promise<{} | undefined>
  onPodUpload: (shipment: IShipmentPodUpdate) => Promise<IGetShipmentResponse | undefined>
  onSubmitLocation: ({
    externalStopId,
    isManual,
    latitude,
    longitude,
  }: ISubmitOneTimeLocation) => Promise<IGetShipmentResponse | undefined>
  shipmentUpdateErrors?: string
  isTimeZoneError?: boolean
}

export const ShareLoadDetailsDisplay = memo<ShareLoadDetailsDisplayProps>(
  ({
    hasToken: isDriverView,
    isLoading,
    isShipmentUpdating,
    isTimeZoneError,
    load,
    onEdit,
    onPodUpload,
    onSubmit,
    onSubmitLocation,
    shareToken,
    shipmentUpdateErrors,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    // Shipment links with tokens are only sent to drivers via SMS
    const {
      externalId,
      notes,
      motorCarrier,
      dispatcher,
      truck,
      trailer,
      status: loadStatus = LOAD_STATE.Pending,
      currentStopSequence,
      rate,
      miles,
      trailerType,
      trailerLength,
      customer,
      customerContact,
      temperatureSetting,
      stops = [],
      drivers = [],
      pods = [],
      isExternal,
      readOnly = true,
    } = load || {}
    const status = obscurePostDeliveryStatus(loadStatus, isExternal)

    const [firstDriver, secondDriver] = drivers || []
    const {
      closeFormHandler,
      currentStop,
      editFormHandler,
      formState,
      isFormOpen,
    } = useDriverInOutForm()
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isFmsOneTimeSmsLoadShareEnabled } = getFlag('fmsOneTimeSmsLoadShare')

    const nextStatusData = getNextDriverStatus(
      status as LOAD_STATE,
      parseInt(currentStopSequence || '1', 10),
      stops
    )
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const stopNumbers = useMemo(() => getStopNumbers(stops), [stops.length])

    const [explicitlySendingLocation, setExplicitlySendingLocation] = useState<boolean>(false)

    const uploadFilesHandler = async (pkgFiles: IFile[] = []): Promise<void> => {
      const allAttachments = addFileTypeToDocumentList([...pods, ...pkgFiles], 'pod')
      const { shipment } = (await onPodUpload({ allAttachments })) || {}
      const { id } = shipment || {}
      if (id) {
        openToast({
          toastType: 'default',
          toastMessage: <div className='body-bold'>POD Uploaded.</div>,
        })
        trackEvent('POD Upload', 'POD_UPLOADED_DRIVER_VIEW')
      } else {
        openToast({ toastType: 'alert', toastMessage: 'POD Upload failed.' })
      }
    }

    const submitLocation = async (
      pos: GeolocationPosition,
      isManual: boolean,
      externalStopId?: string
    ) => {
      const { coords } = pos || {}
      const { latitude, longitude } = coords || {}

      await onSubmitLocation({ latitude, longitude, isManual, externalStopId })
    }

    const submitLocationImplicit = async (pos: GeolocationPosition, externalStopId: string) => {
      await submitLocation(pos, false, externalStopId)
    }

    const submitLocationExplicit = async (pos: GeolocationPosition) => {
      try {
        await submitLocation(pos, true)
      } catch (e) {
        setExplicitlySendingLocation(false)
        openToast({
          toastType: 'alert',
          toastMessage: `Location Failed to Process Internally`,
        })
        trackEvent('One Time Locations', 'FAILED_TO_PROCESS_EXPLICIT_LOCATION')
        return
      }

      setExplicitlySendingLocation(false)
      openToast({
        toastType: 'default',
        toastMessage: <div className='body-bold'>Location Sent</div>,
      })
    }

    const failToDetermineLocationExplicit = async () => {
      let alertMessage

      // navigator.permissions.query is still experimental an doesn't work for every os + browser combination.
      let result
      try {
        result = await navigator.permissions.query({ name: 'geolocation' })
      } catch (e) {
        result = null
      }

      if (!result || result.state === 'denied') {
        alertMessage = (
          <div>
            <p>
              <strong>Failed to send location</strong>
            </p>
            <p>Check your location sharing settings</p>
          </div>
        )
      } else if (result.state === 'prompt') {
        alertMessage = (
          <div>
            <p>
              <strong>Failed to send location</strong>
            </p>
            <p>Try again and accept location sharing</p>
          </div>
        )
      } else {
        alertMessage = 'Failed to send location'
      }

      openToast({
        toastType: 'alert',
        toastMessage: alertMessage,
      })

      trackEvent('One Time Locations', 'FAILED_TO_COLLECT_EXPLICIT_LOCATION')

      setExplicitlySendingLocation(false)
    }

    const sendLocationImplicit = (externalStopId: string) => {
      navigator.geolocation.getCurrentPosition((position: GeolocationPosition): void => {
        submitLocationImplicit(position, externalStopId)
      })
    }

    const sendLocationExplicit = () => {
      trackEvent('One Time Locations', 'CLICKED_SUBMIT_ONE_TIME_LOCATION_BUTTON')
      setExplicitlySendingLocation(true)
      navigator.geolocation.getCurrentPosition(
        submitLocationExplicit,
        failToDetermineLocationExplicit
      )
    }

    const uploadPodHandler = async (e: ChangeEvent<HTMLInputElement>) => {
      const { target } = e || {}
      const { files } = target || {}
      if (!files) return
      const fileList = Array.from(files)
      Promise.all(filePromises(fileList)).then(async packageFiles => {
        const pkgFiles = packageFiles.map(pkg => {
          const pkgFile = pkg as IFile
          pkgFile.uuid = uuid()
          return pkgFile
        })
        uploadFilesHandler(pkgFiles)
      })
    }

    const editStatusFormHandler = () => {
      // dispatched stop sequence is undefined
      const sequence = currentStopSequence || '1'
      const nextStop = stops.find(stop => stop.stopSequence === parseInt(sequence, 10)) || stops[0]
      editFormHandler(DRIVER_IN_OUT_FORM_STATE.statusUpdate, nextStop)
    }
    const ctaStatusUpdates = [...CTA_STATUS_UPDATE_STATES, LOAD_STATE['Ready for Dispatch']]
    const isStatusDrawerOpen = ctaStatusUpdates.includes(status)
    const classes = classNames({
      'share-load-bottom-padding ': isStatusDrawerOpen,
    })

    const deletePodHandler = async (event: MouseEvent<HTMLButtonElement>) => {
      if (event?.currentTarget.id) {
        const id = parseInt(event?.currentTarget.id, 10)
        const newPods = mapRemovedFiles(pods, id)
        const newPodsWithFileType = addFileTypeToDocumentList(newPods, 'pod')
        try {
          await onPodUpload({ allAttachments: [...newPodsWithFileType] })
          openToast({
            toastType: 'default',
            toastMessage: <div className='body-bold'>POD Deleted.</div>,
          })
        } catch (error) {
          openToast({ toastType: 'alert', toastMessage: error })
        }
      }
    }

    const [showTrailerForm, setShowTrailerForm] = useState<boolean>(false)

    const closeTrailerFormHandler = () => {
      setShowTrailerForm(false)
      openToast({
        toastType: 'default',
        toastMessage: <div className='body-bold'>Trailer Updated.</div>,
      })
    }

    const trailerPromptClickHandler = () => {
      setShowTrailerForm(true)
      closeFormHandler()
    }

    const styles = useTopnavStyles()

    return (
      <>
        <TopNav
          showBackButton
          showMenu={false}
          title={`Load # ${externalId || DISPLAY.empty}`}
          contentClassName={styles.topNav}>
          <>
            {!readOnly && !isExternal && (
              <div className={styles.uploadButton}>
                <AddAttachment
                  onChange={uploadPodHandler}
                  text={isShipmentUpdating ? 'Loading...' : 'Upload POD'}
                  disabled={isShipmentUpdating}
                />
              </div>
            )}
          </>
        </TopNav>
        {isLoading ? <Spinner position='relative' /> : ''}
        {!isLoading && (
          <div className={classes}>
            <Container hidePadding>
              <div className='share-load-container pa4'>
                {!!pods.length && isExternal === false && readOnly && (
                  <div className='col-lg-8 col-xs-12'>
                    <AttachmentList
                      attachments={pods}
                      showIcon={false}
                      onFileDelete={deletePodHandler}
                      getTitle={file => `POD: ${file?.filename}`}
                    />
                  </div>
                )}
                {/* Only visible for external loads. */}
                <LoadDetailsFileHeader load={load} onUploadFiles={uploadFilesHandler} />
                {readOnly && isFmsOneTimeSmsLoadShareEnabled ? (
                  <>
                    {stops.length && (
                      <div className={`${styles.readOnly} status-tag mt2 pl4`}>
                        <LoadStatusTag
                          status={status}
                          size={TAG_SIZE.medium}
                          stops={stops}
                          currentStopSequence={Number(currentStopSequence)}
                        />
                      </div>
                    )}
                    <div className='share-load-summary'>
                      <StopsSummary stops={stops} />
                    </div>
                  </>
                ) : (
                  <>
                    {stops.length && (
                      <div className='status-tag mt2 pl4'>
                        <LoadStatusTag
                          status={status}
                          size={TAG_SIZE.medium}
                          stops={stops}
                          currentStopSequence={Number(currentStopSequence)}
                        />
                      </div>
                    )}
                    <div className='share-load-summary'>
                      <StopsSummary stops={stops} />
                    </div>
                  </>
                )}

                <div className='share-load-details col-lg-8 col-xs-12'>
                  <Card className='mt4 mb4'>
                    <div className='pl1'>
                      <DetailsHeader />
                    </div>
                    <Details
                      isDriverView={isDriverView}
                      isSharedLoadView
                      rate={rate}
                      miles={miles}
                      temperatureSetting={temperatureSetting}
                      customer={customer}
                      customerContact={customerContact}
                      externalId={externalId}
                      trailerType={trailerType}
                      trailerLength={trailerLength}
                    />
                  </Card>
                  {stops.map((stop, idx) => {
                    return (
                      <ShareLoadDetailsStop
                        isExternal={isExternal}
                        key={stop?.id}
                        onInOutTimeClick={editFormHandler}
                        stop={stop}
                        sequence={stopNumbers[idx]}
                      />
                    )
                  })}
                  <Card className='mt4 mb4'>
                    <NotesHeader />
                    <InlineRow rowTitle='Notes' rowBody={<NotesDisplay notes={notes} />} />
                  </Card>
                </div>
                <div className='fp-load-details-status col-lg-4 col-xs-12 pv2 ph2'>
                  {stops.length && (
                    <Card>
                      <div className='share-load-status-tag-card col-md-12 col-xs-12 mb4'>
                        <LoadStatusHeader />
                      </div>
                      <div className='share-load-status-tag-card col-md-12 col-xs-12 mb4'>
                        <LoadStatusTag
                          status={status}
                          size={TAG_SIZE.xsmall}
                          stops={stops}
                          currentStopSequence={Number(currentStopSequence)}
                        />
                      </div>
                      <div className='col-md-12 col-xs-12 mb4'>
                        <DriverInlineDisplay
                          label={secondDriver ? 'Driver 1' : 'Driver'}
                          value={firstDriver}
                        />
                      </div>
                      {secondDriver && (
                        <div className='col-md-12 col-xs-12 mb4'>
                          <DriverInlineDisplay label='Driver 2' value={secondDriver} />
                        </div>
                      )}
                      <div className='col-md-12 col-xs-12 mb4'>
                        <DispatcherInlineDisplay value={dispatcher} />
                      </div>
                      <div className='col-md-12 col-xs-12 share-load-carrier'>
                        <CarrierInlineDisplay value={motorCarrier?.legalName} />
                      </div>
                      <div className='col-md-12 col-xs-6 share-load-truck mb4'>
                        <TruckInlineDisplay value={truck} />
                      </div>
                      <div className='col-md-12 col-xs-6 share-load-trailer mb4'>
                        <TrailerInlineDisplay value={trailer} />
                      </div>
                    </Card>
                  )}
                </div>
              </div>
            </Container>
          </div>
        )}
        {!readOnly && (
          <>
            <Toast
              variation={toastType}
              open={toastOpen}
              onClose={closeToast}
              message={toastMessage}
            />
            <StatusUpdateDrawer
              pods={pods}
              onPodUpload={uploadPodHandler}
              isOpen={isStatusDrawerOpen}
              loadStatus={status}
              nextStatusData={nextStatusData}
              onCtaClick={editStatusFormHandler}
              onSendLocationClick={sendLocationExplicit}
              showLocationLoading={explicitlySendingLocation}
              isExternal={!!isExternal}
            />
            {isFormOpen && (
              <InOutTimesForm
                isShipmentUpdating={isShipmentUpdating}
                shipmentUpdateErrors={shipmentUpdateErrors}
                currentStopSequence={currentStopSequence}
                nextStatusData={nextStatusData}
                onToastOpen={openToast}
                onEdit={onEdit}
                onSubmit={onSubmit}
                sendLocation={sendLocationImplicit}
                formState={formState}
                stops={stops}
                currentStop={currentStop}
                isOpen={isFormOpen}
                onClose={closeFormHandler}
                isTimeZoneError={isTimeZoneError}
                isExternal={!!isExternal}
                status={status}
                onClickTrailerPrompt={trailerPromptClickHandler}
              />
            )}
            {showTrailerForm && (
              <TrailerNumberForm
                isOpen={showTrailerForm}
                onClose={closeTrailerFormHandler}
                shareToken={shareToken}
                trailer={trailer}
              />
            )}
          </>
        )}
      </>
    )
  }
)
